import { z } from 'zod';

const Code400ConstSchema = z.object({
  BAD_REQUEST: z.literal('BAD_REQUEST'),
  MISSING_HTTP_HEADER: z.literal('MISSING_HTTP_HEADER'),
  INVALID_HTTP_HEADER: z.literal('INVALID_HTTP_HEADER'),
  MALFORMED_PATH_PARAM: z.literal('MALFORMED_PATH_PARAM'),
  MISSING_QUERY_PARAM: z.literal('MISSING_QUERY_PARAM'),
  INVALID_QUERY_PARAM: z.literal('INVALID_QUERY_PARAM'),
  MALFORMED_QUERY_PARAM: z.literal('MALFORMED_QUERY_PARAM'),
  CONFIGURATION_ERROR: z.literal('CONFIGURATION_ERROR'),
});
const Code401ConstSchema = z.object({
  UNAUTHORISED: z.literal('UNAUTHORISED'),
});
const Code403ConstSchema = z.object({
  ACCESS_DENIED: z.literal('ACCESS_DENIED'),
});
const Code404ConstSchema = z.object({
  RESOURCE_NOT_FOUND: z.literal('RESOURCE_NOT_FOUND'),
});
const Code409ConstSchema = z.object({
  CONFLICT: z.literal('CONFLICT'),
  IDEMPOTENCY_CONFLICT: z.literal('IDEMPOTENCY_CONFLICT'),
  RESOURCE_NOT_MODIFIABLE: z.literal('RESOURCE_NOT_MODIFIABLE'),
});
const Code422ConstSchema = z.object({
  VALIDATION_FAILURE: z.literal('VALIDATION_FAILURE'),
});
const Code424ConstSchema = z.object({
  FAILED_DEPENDENCY: z.literal('FAILED_DEPENDENCY'),
  UNAUTHORISED: z.literal('UNAUTHORISED'),
  RETRY_LIMIT_EXCEEDED: z.literal('RETRY_LIMIT_EXCEEDED'),
});
const Code500ConstSchema = z.object({
  INTERNAL_SERVER_ERROR: z.literal('INTERNAL_SERVER_ERROR'),
});

const ValidationErrorTypeSchema = z.union([
  z.literal('CHECKSUM_FAILED'),
  z.literal('FIELD_NOT_ALLOWED'),
  z.literal('INVALID_DATE'),
  z.literal('INVALID_FORMAT'),
  z.literal('INVALID_TIME'),
  z.literal('INVALID_TYPE'),
  z.literal('INVALID_VALUE'),
  z.literal('IS_EQUAL'),
  z.literal('MISSING_FIELD'),
  z.literal('NOT_EQUAL'),
  z.literal('NOT_UNIQUE'),
  z.literal('OUT_OF_RANGE'),
  z.literal('REGEX_FAILED'),
  z.literal('TOO_FEW'),
  z.literal('TOO_HIGH'),
  z.literal('TOO_LONG'),
  z.literal('TOO_LOW'),
  z.literal('TOO_MANY'),
  z.literal('TOO_SHORT'),
  z.literal('VALUE_REQUIRED'),
]);

const Error400Schema = z.object({
  status: z.literal(400),
  data: z.object({
    code: Code400ConstSchema.keyof(),
    message: z.string(),
  }),
});

const Error401Schema = z.object({
  status: z.literal(401),
  data: z.object({
    code: Code401ConstSchema.keyof(),
    message: z.string(),
  }),
});

const Error403Schema = z.object({
  status: z.literal(403),
  data: z.object({
    code: Code403ConstSchema.keyof(),
    message: z.string(),
  }),
});

const Error404Schema = z.object({
  status: z.literal(404),
  data: z.object({
    code: Code404ConstSchema.keyof(),
    message: z.string(),
  }),
});

const Error409Schema = z.object({
  status: z.literal(409),
  data: z.object({
    code: Code409ConstSchema.keyof(),
    message: z.string(),
  }),
});

export const Error422Schema = z.object({
  status: z.literal(422),
  data: z.object({
    code: Code422ConstSchema.keyof(),
    message: z.string(),
    errors: z.array(
      z.object({
        type: ValidationErrorTypeSchema,
        propertyPath: z.string(),
        message: z.string(),
      })
    ),
  }),
});

const Error424Schema = z.object({
  status: z.literal(424),
  data: z.object({
    code: Code424ConstSchema.keyof(),
    message: z.string(),
  }),
});

const Error500Schema = z.object({
  status: z.literal(500),
  data: z.object({
    code: Code500ConstSchema.keyof(),
    message: z.string(),
  }),
});

export const GlobalApiErrorSchema = z.union([
  Error400Schema,
  Error401Schema,
  Error403Schema,
  Error404Schema,
  Error409Schema,
  Error422Schema,
  Error424Schema,
  Error500Schema,
]);

export const GlobalApiErrorCodeSchema = z.union([
  Code400ConstSchema.keyof(),
  Code401ConstSchema.keyof(),
  Code403ConstSchema.keyof(),
  Code404ConstSchema.keyof(),
  Code409ConstSchema.keyof(),
  Code422ConstSchema.keyof(),
  Code424ConstSchema.keyof(),
  Code500ConstSchema.keyof(),
]);

export const ErrorCodesByResponseStatusSchema = z.object({
  CODE_400: Code400ConstSchema,
  CODE_401: Code401ConstSchema,
  CODE_403: Code403ConstSchema,
  CODE_404: Code404ConstSchema,
  CODE_409: Code409ConstSchema,
  CODE_422: Code422ConstSchema,
  CODE_424: Code424ConstSchema,
  CODE_500: Code500ConstSchema,
});
